/**
 * useChatFileBlobUrl — a browser-usable ``blob:`` URL for a chat-file's bytes,
 * fetched through the backend auth-proxy (``getChatFileBlob``).
 *
 * Why not the presigned URL: ``MINIO_PUBLIC_ENDPOINT`` host-swaps the signed
 * URL post-signature, and SigV4 signs the ``Host`` header → the browser fetch
 * fails with ``SignatureDoesNotMatch`` (the raw S3 XML then leaks into the PDF
 * panel). The proxy streams the bytes via the internal endpoint (valid
 * signature server-side); here we wrap them in an object URL for the viewer
 * and REVOKE it on cleanup so blobs don't leak.
 *
 * Leaf module (no chat-barrel deps) so run-surface consumers stay lightweight.
 */

import { getChatFileBlob } from "@ando/core";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

export interface UseChatFileBlobUrl {
  /** Object URL for the fetched bytes, or ``undefined`` while loading/on error. */
  readonly url: string | undefined;
  /** The fetched blob's MIME (``blob.type``) — drives multi-format preview. */
  readonly mimeType: string | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
}

/**
 * Fetch ``contentId``'s bytes as a blob and expose a stable object URL.
 * Passing ``undefined`` disables the fetch (the run surface only sometimes
 * has a triggering document).
 */
export function useChatFileBlobUrl(contentId: string | undefined): UseChatFileBlobUrl {
  const query = useQuery({
    queryKey: ["chat-file-blob", contentId],
    queryFn: () => {
      if (!contentId) throw new Error("useChatFileBlobUrl: contentId is required");
      return getChatFileBlob(contentId);
    },
    enabled: Boolean(contentId),
    // Bytes don't change for a given content_id — fetch once, keep it.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 10 * 60_000,
    retry: 1,
  });

  const blob = query.data;
  const [url, setUrl] = React.useState<string | undefined>(undefined);
  React.useEffect(() => {
    if (!blob) {
      setUrl(undefined);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  return {
    url,
    mimeType: blob?.type || undefined,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
